import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  Plus,
  Trash2,
  RefreshCw,
  ExternalLink,
  Camera,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  Image,
} from 'lucide-react';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { trpc } from '@/lib/api/trpc';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface ManagePreviewsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deploymentId: string;
  pluginName: string;
  version: string;
  storeId: string;
}

export function ManagePreviewsDialog({
  open,
  onOpenChange,
  deploymentId,
  pluginName,
  version,
  storeId,
}: ManagePreviewsDialogProps) {
  const { t } = useTranslation('storefront');
  const { toast } = useToast();
  const utils = trpc.useUtils();

  // Form state
  const [selectedPagePath, setSelectedPagePath] = useState<string>('');
  const [path, setPath] = useState('');
  const [name, setName] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [presetName, setPresetName] = useState('');
  const [description, setDescription] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Fetch demo previews (includes deployment info)
  const { data, isLoading, refetch } = trpc.plugins.getDemoPreviews.useQuery(
    { deploymentId },
    { enabled: open },
  );

  // Extract deployment pages from the plugins.list query instead
  const { data: pluginsData } = trpc.plugins.list.useQuery({ storeId }, { enabled: open });

  // Find the current deployment from the plugins list
  const currentDeployment = pluginsData?.plugins
    ?.flatMap((p: any) => p.deployments || [])
    .find((d: any) => d.deploymentId === deploymentId);

  const availablePages = currentDeployment?.pages || [];
  const loadingDeployment = isLoading && !pluginsData;

  // Auto-fill path and name when a page is selected
  useEffect(() => {
    if (selectedPagePath && availablePages.length > 0) {
      const selectedPage = availablePages.find((p: any) => p.path === selectedPagePath);
      if (selectedPage) {
        setPath(selectedPage.path);
        setName(selectedPage.name || '');
      }
    }
  }, [selectedPagePath, availablePages]);

  // Trigger screenshots mutation (defined first so it can be used in addPreviewMutation)
  const triggerSnapshotsMutation = trpc.plugins.triggerPreviewSnapshots.useMutation({
    onSuccess: (result) => {
      console.log('[ManagePreviewsDialog] Screenshot capture triggered successfully:', result);
      toast({
        title: t('plugins.managePreviews.capturingScreenshots'),
        description: t('plugins.managePreviews.screenshotsInBackground'),
      });
      // Start polling for updates
      const interval = setInterval(() => {
        refetch();
      }, 3000);

      // Stop polling after 30 seconds
      setTimeout(() => clearInterval(interval), 30000);
    },
    onError: (error) => {
      console.error('[ManagePreviewsDialog] Error triggering screenshots:', error);
      toast({
        title: t('plugins.managePreviews.error'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Add demo preview mutation
  const addPreviewMutation = trpc.plugins.addDemoPreview.useMutation({
    onSuccess: () => {
      toast({
        title: t('plugins.managePreviews.success'),
        description: t('plugins.managePreviews.previewAdded'),
      });
      // Reset form
      setSelectedPagePath('');
      setPath('');
      setName('');
      setPreviewUrl('');
      setPresetName('');
      setDescription('');
      setShowAdvanced(false);
      // Refetch data and start polling to see when screenshots are ready
      refetch();
      // Start polling for updates
      const interval = setInterval(() => {
        refetch();
      }, 3000);
      // Stop polling after 30 seconds
      setTimeout(() => clearInterval(interval), 30000);
    },
    onError: (error) => {
      toast({
        title: t('plugins.managePreviews.error'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Remove preview mutation
  const removePreviewMutation = trpc.plugins.removeDemoPreview.useMutation({
    onSuccess: () => {
      toast({
        title: t('plugins.managePreviews.success'),
        description: t('plugins.managePreviews.previewRemoved'),
      });
      refetch();
    },
    onError: (error) => {
      toast({
        title: t('plugins.managePreviews.error'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleAddPreview = () => {
    if (!path || !previewUrl) {
      toast({
        title: t('plugins.managePreviews.error'),
        description: t('plugins.managePreviews.pathAndUrlRequired'),
        variant: 'destructive',
      });
      return;
    }

    addPreviewMutation.mutate({
      deploymentId,
      path,
      name: name || undefined,
      previewUrl,
      metadata: {
        presetName: presetName || undefined,
        description: description || undefined,
      },
    });
  };

  const handleRemovePreview = (path: string, previewId: string) => {
    removePreviewMutation.mutate({
      deploymentId,
      path,
      previewId,
    });
  };

  const handleCaptureScreenshots = () => {
    // Force re-capture if there are no pending screenshots (all are already captured)
    const force = stats ? stats.pending === 0 && stats.total > 0 : false;
    console.log(
      `[ManagePreviewsDialog] Triggering screenshots for deployment: ${deploymentId} (force: ${force})`,
    );
    triggerSnapshotsMutation.mutate({ deploymentId, force });
  };

  const demoPreviews = data?.demoPreviews;
  const stats = data?.stats;
  const selectedPageInfo = availablePages.find((p: any) => p.path === selectedPagePath);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[92dvh] w-full max-w-[calc(100%-1rem)] flex-col gap-0 sm:max-w-[90vw] md:max-w-4xl lg:max-w-5xl">
          <DialogHeader className="flex-shrink-0 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2.5 text-lg">
                  <Camera className="h-5 w-5" />
                  {t('plugins.managePreviews.previewScreenshots')}
                </DialogTitle>
                <DialogDescription className="mt-0.5 text-sm">
                  {pluginName} · v{version}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="-mx-1 flex-1 space-y-3 overflow-y-auto px-1 pb-1">
            {/* Stats & Action Bar */}
            {stats && stats.total > 0 && (
              <div className="bg-muted/40 flex items-center justify-between rounded-lg border px-4 py-2.5 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Image className="text-muted-foreground h-4 w-4" aria-hidden="true" />
                    <span className="font-semibold">{stats.total}</span>
                    <span className="text-muted-foreground">{t('plugins.managePreviews.total')}</span>
                  </div>
                  {stats.captured > 0 && (
                    <>
                      <div className="bg-border h-5 w-px" />
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span className="font-semibold">{stats.captured}</span>
                        <span className="text-muted-foreground">{t('plugins.managePreviews.captured')}</span>
                      </div>
                    </>
                  )}
                  {stats.pending > 0 && (
                    <>
                      <div className="bg-border h-5 w-px" />
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="text-muted-foreground h-4 w-4" />
                        <span className="font-semibold">{stats.pending}</span>
                        <span className="text-muted-foreground">{t('plugins.managePreviews.pending')}</span>
                      </div>
                    </>
                  )}
                  {stats.failed > 0 && (
                    <>
                      <div className="bg-border h-5 w-px" />
                      <div className="flex items-center gap-2 text-sm">
                        <XCircle className="h-4 w-4 text-red-600" />
                        <span className="font-semibold">{stats.failed}</span>
                        <span className="text-muted-foreground">{t('plugins.managePreviews.failed')}</span>
                      </div>
                    </>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCaptureScreenshots}
                  disabled={triggerSnapshotsMutation.isPending}
                  className="h-8"
                  title={
                    stats.pending === 0 && stats.total > 0
                      ? t('plugins.managePreviews.reCaptureAll')
                      : t('plugins.managePreviews.capturePending')
                  }
                >
                  {triggerSnapshotsMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Camera className="mr-2 h-4 w-4" />
                  )}
                  {stats.pending === 0 && stats.total > 0 ? t('plugins.managePreviews.reCaptureAll') : t('plugins.managePreviews.capture')}
                  {stats.pending > 0 && (
                    <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-[10px]">
                      {stats.pending}
                    </Badge>
                  )}
                </Button>
              </div>
            )}

            {/* Preview List - TOP */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
              </div>
            ) : demoPreviews && demoPreviews.pages.length > 0 ? (
              <div className="space-y-2">
                {demoPreviews.pages.map((page) => (
                  <Card key={page.path} className="overflow-hidden shadow-sm">
                    <CardContent className="p-0">
                      {/* Page Header */}
                      <div className="bg-muted/40 flex items-center gap-2 border-b px-3 py-2">
                        <code className="text-sm font-semibold">{page.path}</code>
                        {page.name && <span className="text-muted-foreground text-sm">· {page.name}</span>}
                        <div className="bg-border ml-auto h-4 w-px" />
                        <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-medium">
                          {t('plugins.managePreviews.previewCount', { count: page.previews.length })}
                        </Badge>
                      </div>

                      {/* Preview Items */}
                      <div className="divide-y">
                        {page.previews.map((preview) => (
                          <div key={preview.id} className="group flex gap-3 p-3">
                            {/* Status Indicator */}
                            <div className="flex shrink-0 items-start pt-1">
                              {preview.metadata?.status === 'captured' ? (
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                              ) : preview.metadata?.status === 'failed' ? (
                                <XCircle className="h-4 w-4 text-red-600" />
                              ) : (
                                <Clock className="text-muted-foreground h-4 w-4" />
                              )}
                            </div>

                            {/* Screenshot Thumbnails - BIGGER */}
                            <div className="flex shrink-0 gap-2.5">
                              {/* Desktop Thumbnail */}
                              {preview.screenshotUrls?.desktop ? (
                                <a
                                  href={preview.screenshotUrls.desktop}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="group/thumb relative block overflow-hidden rounded-md border shadow-sm transition-shadow hover:shadow-md"
                                >
                                  {/* eslint-disable-next-line @next/next/no-img-element */}
                                  <img
                                    src={preview.screenshotUrls.desktop}
                                    alt={t('plugins.managePreviews.desktopScreenshotAlt')}
                                    className="h-32 w-48 object-cover object-top transition-transform group-hover/thumb:scale-105"
                                  />
                                  <div className="bg-background/90 absolute inset-x-0 bottom-0 px-2 py-1 text-center text-[10px] font-medium backdrop-blur-sm">
                                    {t('plugins.managePreviews.desktop')}
                                  </div>
                                </a>
                              ) : (
                                <div className="bg-muted/50 flex h-32 w-48 flex-col items-center justify-center gap-2 rounded-md border border-dashed">
                                  {preview.metadata?.status === 'pending' ? (
                                    <>
                                      <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
                                      <span className="text-muted-foreground text-xs font-medium">
                                        {t('plugins.managePreviews.capturing')}
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <Image
                                        className="text-muted-foreground/40 h-8 w-8"
                                        aria-hidden="true"
                                      />
                                      <span className="text-muted-foreground text-xs font-medium">
                                        {t('plugins.managePreviews.desktop')}
                                      </span>
                                    </>
                                  )}
                                </div>
                              )}

                              {/* Mobile Thumbnail */}
                              {preview.screenshotUrls?.mobile ? (
                                <a
                                  href={preview.screenshotUrls.mobile}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="group/thumb relative block overflow-hidden rounded-md border shadow-sm transition-shadow hover:shadow-md"
                                >
                                  {/* eslint-disable-next-line @next/next/no-img-element */}
                                  <img
                                    src={preview.screenshotUrls.mobile}
                                    alt={t('plugins.managePreviews.mobileScreenshotAlt')}
                                    className="h-32 w-20 object-cover object-top transition-transform group-hover/thumb:scale-105"
                                  />
                                  <div className="bg-background/90 absolute inset-x-0 bottom-0 px-1 py-1 text-center text-[10px] font-medium backdrop-blur-sm">
                                    {t('plugins.managePreviews.mobile')}
                                  </div>
                                </a>
                              ) : (
                                <div className="bg-muted/50 flex h-32 w-20 flex-col items-center justify-center gap-2 rounded-md border border-dashed">
                                  {preview.metadata?.status === 'pending' ? (
                                    <>
                                      <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
                                      <span className="text-muted-foreground text-[10px] font-medium">
                                        {t('plugins.managePreviews.capturing')}
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <Image
                                        className="text-muted-foreground/40 h-6 w-6"
                                        aria-hidden="true"
                                      />
                                      <span className="text-muted-foreground text-xs font-medium">
                                        {t('plugins.managePreviews.mobile')}
                                      </span>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Preview Info - DE-EMPHASIZED */}
                            <div className="min-w-0 flex-1 space-y-1.5">
                              {/* Metadata Row - MORE PROMINENT */}
                              <div className="flex flex-wrap items-center gap-1.5">
                                {preview.metadata?.presetName && (
                                  <code className="bg-primary/10 text-primary rounded px-2 py-1 text-xs font-semibold">
                                    {preview.metadata.presetName}
                                  </code>
                                )}
                                {preview.metadata?.description && (
                                  <span className="text-sm font-medium">{preview.metadata.description}</span>
                                )}
                              </div>

                              {/* Preview URL - LESS PROMINENT */}
                              <div className="flex items-center gap-1.5">
                                <ExternalLink className="text-muted-foreground h-3 w-3 shrink-0" />
                                <a
                                  href={preview.previewUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-foreground line-clamp-1 text-xs transition-colors"
                                  title={preview.previewUrl}
                                >
                                  {preview.previewUrl}
                                </a>
                              </div>

                              {/* Error message if failed */}
                              {preview.metadata?.status === 'failed' && preview.metadata?.error && (
                                <p className="text-xs text-red-600">{preview.metadata.error}</p>
                              )}
                            </div>

                            {/* Delete Button */}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRemovePreview(page.path, preview.id)}
                              disabled={removePreviewMutation.isPending}
                              className="hover:bg-destructive/10 hover:text-destructive h-8 w-8 shrink-0 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : null}

            {/* Add New Preview Form - BOTTOM */}
            <Separator />
            <Card className="shadow-sm">
              <CardContent className="space-y-3 px-4 pb-4 pt-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    <h3 className="text-sm font-semibold">{t('plugins.managePreviews.addNewPreview')}</h3>
                  </div>
                  {availablePages.length > 0 && (
                    <span className="text-muted-foreground text-xs">
                      {t('plugins.managePreviews.pagesAvailable', { count: availablePages.length })}
                    </span>
                  )}
                </div>

                <div className="space-y-3">
                  {/* Page Selector */}
                  {availablePages.length > 0 && (
                    <div className="space-y-1.5">
                      <Label htmlFor="pageSelector" className="text-sm">
                        {t('plugins.managePreviews.selectPage')}
                      </Label>
                      <Select value={selectedPagePath} onValueChange={setSelectedPagePath}>
                        <SelectTrigger id="pageSelector" className="h-9">
                          <SelectValue placeholder={t('plugins.managePreviews.choosePage')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectLabel>{t('plugins.managePreviews.availablePages')}</SelectLabel>
                            {availablePages.map((page: any) => (
                              <SelectItem key={page.path} value={page.path}>
                                <code className="text-xs">{page.path}</code>
                                {page.name && (
                                  <span className="text-muted-foreground ml-2 text-xs">· {page.name}</span>
                                )}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Preview URL - Main Field */}
                  <div className="space-y-1.5">
                    <Label htmlFor="previewUrl" className="text-sm">
                      {t('plugins.managePreviews.previewUrl')} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="previewUrl"
                      placeholder="https://example.com/checkout?preview=true"
                      value={previewUrl}
                      onChange={(e) => setPreviewUrl(e.target.value)}
                      disabled={loadingDeployment}
                      className="h-9"
                    />
                  </div>

                  {/* Advanced Mode Toggle */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="text-muted-foreground -ml-2 h-8 w-auto justify-start text-xs hover:bg-transparent"
                  >
                    {showAdvanced ? (
                      <ChevronDown className="mr-1.5 h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    {t('plugins.managePreviews.advancedOptions')}
                  </Button>

                  {/* Advanced Fields - Collapsible */}
                  {showAdvanced && (
                    <div className="bg-muted/40 space-y-2.5 rounded-lg border p-3">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label htmlFor="path" className="text-xs">
                            {t('plugins.managePreviews.pagePath')}
                          </Label>
                          <Input
                            id="path"
                            placeholder="/checkout"
                            value={path}
                            onChange={(e) => setPath(e.target.value)}
                            disabled={loadingDeployment}
                            className="h-8 text-sm"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor="name" className="text-xs">
                            {t('plugins.managePreviews.pageName')}
                          </Label>
                          <Input
                            id="name"
                            placeholder="Checkout Page"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={loadingDeployment}
                            className="h-8 text-sm"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor="presetName" className="text-xs">
                            {t('plugins.managePreviews.presetName')}
                          </Label>
                          <Input
                            id="presetName"
                            placeholder="premium"
                            value={presetName}
                            onChange={(e) => setPresetName(e.target.value)}
                            disabled={loadingDeployment}
                            className="h-8 text-sm"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor="description" className="text-xs">
                            {t('plugins.managePreviews.description')}
                          </Label>
                          <Input
                            id="description"
                            placeholder="Brief description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={loadingDeployment}
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Add Button */}
                  <Button
                    onClick={handleAddPreview}
                    disabled={addPreviewMutation.isPending || loadingDeployment}
                    className="h-9 w-full"
                  >
                    {addPreviewMutation.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                    <Plus className="mr-2 h-3.5 w-3.5" />
                    {t('plugins.managePreviews.addPreview')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Empty state if no previews */}
            {!isLoading && (!demoPreviews || demoPreviews.pages.length === 0) && (
              <div className="bg-muted/20 text-muted-foreground flex flex-col items-center justify-center gap-2.5 rounded-lg border border-dashed py-12 text-center">
                <Camera className="h-8 w-8 opacity-40" aria-hidden="true" />
                <div className="space-y-0.5">
                  <p className="text-sm font-semibold">{t('plugins.managePreviews.noPreviewsYet')}</p>
                  <p className="text-xs">{t('plugins.managePreviews.addPreviewToStart')}</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-shrink-0 justify-end border-t pt-3">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              {t('plugins.managePreviews.close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
